import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import * as XLSX from "xlsx";
import { differenceInDays, parse } from "date-fns";
import Layout from '../../components/Layout';
import { maintenanceService, fleetService } from '../../services/firestore';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import IconButton from '../../ui/IconButton';
import ActionMenu from '../../ui/ActionMenu';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table/Table';
import { CellText, CellPlate, CellMuted, CellStatus, toneClass } from '../../ui/DataTable/cells';
import { Field, Input, Select, Textarea } from '../../ui/Field/Field';
import Dialog from '../../ui/Dialog/Dialog';
import EmptyState from '../../ui/EmptyState';
import { toast, confirmDialog } from '../../ui/feedback';
import LoadingSpinner from '../../components/LoadingSpinner';
import RequirePermission from '../../components/RequirePermission';
import { usePermissions } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../constants/permissions';
import { useAlert } from '../../hooks/useAlert';
import logger from '../../utils/logger';
import { adminService } from '../../services/adminService';
import { useAuth } from '../../contexts/AuthContext';
import { normalizePlate as sharedNormalizePlate } from '../../services/possessionMatcher';
import { trueDuplicateGroups, dateInUpdate, openLogFleetWarning } from '../../services/maintenanceLogs.logic';
import { finesService, errorMessage } from '../../services/finesService';
import { dubaiToday } from '../../services/fleetStatus';
import { carsWithoutOpenLog } from './ejarWorkshop.logic';
import EjarWorkshopCard from './EjarWorkshopCard';
import { dubaiDateTime } from '../../utils/dubaiTime';

// Page-level wrapper: this page keys lookups by lower-case-no-spaces plates.
function normalizePlate(plate) {
    return sharedNormalizePlate(plate).toLowerCase();
}



// Function to filter columns with data only
function filterColumnsWithData(rows) {
    if (!Array.isArray(rows) || !rows.length) return rows;
    const keys = Object.keys(rows[0]);
    // Exclude Index and Days Delayed from checking
    const dataKeys = keys.filter(k => k !== "Index" && k !== "Days Delayed");
    const columnsWithData = dataKeys.filter(key => rows.some(row => row[key] && String(row[key]).trim() !== ""));
    return rows.map(row => {
        const filtered = { Index: row.Index };
        columnsWithData.forEach(key => { filtered[key] = row[key]; });
        filtered["Days Delayed"] = row["Days Delayed"];
        return filtered;
    });
}

// Helper function to format date as DD/MM/YYYY
function formatDateDDMMYYYY(date) {
    if (!date) date = new Date();
    if (!(date instanceof Date) || isNaN(date.getTime())) return "";
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
}

const toInputFormat = (str) => {
    if (!str || typeof str !== 'string') return "";

    // Remove any accidental spaces
    str = str.trim();

    const parts = str.split(/[-/]/);
    if (parts.length !== 3) return str;

    let [first, second, third] = parts.map(p => p.trim().padStart(2, '0'));

    // Detect format: if first part is 4 digits, it's YYYY-MM-DD (already input format)
    if (first.length === 4) {
        return `${first}-${second}-${third}`;
    }

    // Otherwise it's DD/MM/YYYY or DD/MM/YY (display format)
    let year = third;

    // Handle 2-digit years: 00-49 = 2000-2049, 50-99 = 1950-1999
    if (year.length === 2) {
        const yearNum = parseInt(year, 10);
        year = yearNum < 50 ? `20${year}` : `19${year}`;
    }

    return `${year}-${second}-${first}`;
};

const toDisplayFormat = (str) => {
    if (!str || typeof str !== 'string') return "";

    const parts = str.split("-");
    if (parts.length === 3 && parts[0].length === 4) {
        const [y, m, d] = parts.map(p => p.trim());
        return `${d}/${m}/${y}`;
    }
    return str;
};

export default function CarsMaintenancePage() {
    // const navigate = useNavigate();
    const { alertSuccess, alertError } = useAlert();
    const { currentUser } = useAuth();
    const { hasPermission } = usePermissions();

    // const [data, setData] = useState([]);
    const [dbRecords, setDbRecords] = useState([]);
    const [search, setSearch] = useState("");
    const [filterMode, setFilterMode] = useState("all");
    const [statusFilter, setStatusFilter] = useState("");
    const [duplicatePlates, setDuplicatePlates] = useState({});
    const [invygoPlates, setInvygoPlates] = useState([]);
    const [duplicateRows, setDuplicateRows] = useState({});
    const [fleetData, setFleetData] = useState([]); // Store full fleet for auto-fill
    // const tableRef = useRef(null);
    const [loading, setLoading] = useState(true);
    // const [sheetError, setSheetError] = useState(null);
    // Google Auth State Removed

    const [activeTab, setActiveTab] = useState('incomplete'); // 'completed' or 'incomplete'

    // Store last uploaded Excel data for auto-fill
    // uploadedStatusMap: { [normalizedPlate]: { status, branch } }
    // Store last uploaded Excel data for auto-fill
    // uploadedStatusMap: { [normalizedPlate]: { status, branch } }
    const [uploadedStatusMap, setUploadedStatusMap] = useState({});

    // Store last used singular values (legacy fallback)
    const [lastExcelData, setLastExcelData] = useState({ status: "", branch: "" });

    // Timestamp for last status upload
    const [statusLastUpdated, setStatusLastUpdated] = useState(null);
    const [newRecord, setNewRecord] = useState({
        Vehicle: "",
        model: "", "Damage Details": "", "Date OUT": "", "Date IN": "",
        Status: "",
        Branch: "",
        Date: formatDateDDMMYYYY() // Today's date in DD/MM/YYYY format
    });

    const [vehicleWarning, setVehicleWarning] = useState(null); // Warning for existing maintenance
    const [suggestions, setSuggestions] = useState([]); // Multiple matches
    const searchTimeout = useRef(null);

    // Edit State
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);

    const handleEditTask = (record) => {
        setEditingRecord({ ...record });
        setIsEditModalOpen(true);
    };

    const handleSaveEdit = async () => {
        if (!editingRecord) return;
        setLoading(true);
        try {
            // the store: saved on the version the log was opened with (changed since: nothing saved, the list re-read)
            const res = await maintenanceService.updateLog(editingRecord.id, {
                "Damage Details": editingRecord["Damage Details"]
            }, { version: editingRecord.version });
            if (res.success) {
                toast.success('Task updated successfully!');
                setIsEditModalOpen(false);
                setEditingRecord(null);
                await refreshDbRecords(true);
            } else {
                if (res.code === 'record-changed') await refreshDbRecords(true);
                alertError('Failed to update task: ' + res.error);
            }
        } catch (e) {
            alertError('Update error: ' + e.message);
        }
        setLoading(false);
    };

    // Import Result Modal State
    // const [importResult, setImportResult] = useState(null);
    // const [viewImportDetails, setViewImportDetails] = useState("updated"); // 'updated' | 'notfound'

    useEffect(() => {
        // Clear the initial loading flag once the first fetch settles (it previously stayed on).
        refreshDbRecords().finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Update newRecord when lastExcelData changes
    useEffect(() => {
        if (lastExcelData.status || lastExcelData.branch) {
            setNewRecord(prev => ({
                ...prev,
                Status: lastExcelData.status || "",
                Branch: lastExcelData.branch || ""
            }));
        }
    }, [lastExcelData]);

    // The same repair entered more than once (Clean Duplicates), always from the rows on screen
    const dbDuplicates = useMemo(() => trueDuplicateGroups(dbRecords), [dbRecords]);

    const refreshDbRecords = async (force = false) => {
        try {
            setDbRecords(await maintenanceService.getAllLogs(force));
        } catch (error) {
            logger.error('Error refreshing DB records:', error);
            alertError('Failed to refresh data: ' + error.message, 'Refresh Error');
        }
    };

    // Only the SAME repair entered twice is a duplicate (same car, same Date OUT, same damage
    // text): grouping by plate alone deleted different open repairs of one car (audit FLT-06).
    // Deleting needs the same permission as the row Delete, and the groups are listed first.
    const handleCleanupDuplicates = async () => {
        if (!hasPermission(PERMISSIONS.delete_maintenance_record)) {
            alertError('You do not have permission to delete maintenance records.', 'Not Allowed');
            return;
        }
        if (!dbDuplicates.length) return;
        const extras = dbDuplicates.reduce((sum, g) => sum + g.remove.length, 0);
        const listed = dbDuplicates.slice(0, 10)
            .map((g) => `${g.plate} (out ${g.dateOut}): "${g.damage}" x${g.remove.length + 1}`)
            .join('; ');
        const more = dbDuplicates.length > 10 ? `; and ${dbDuplicates.length - 10} more group(s)` : '';
        if (!(await confirmDialog({
            title: 'Clean up duplicates?',
            message: `${dbDuplicates.length} repair(s) were entered more than once (same car, same Date OUT, same damage): ${listed}${more}. `
                + `One record of each is kept (the one with a Date IN, else the latest) and ${extras} extra record(s) are deleted permanently. Proceed?`,
            confirmLabel: 'Delete duplicates',
            danger: true,
        }))) return;

        setLoading(true);
        let deletedCount = 0;
        let failedCount = 0;

        try {
            for (const group of dbDuplicates) {
                for (const rec of group.remove) {
                    const res = await maintenanceService.deleteLog(rec.id);
                    if (res && res.success) deletedCount++;
                    else failedCount++;
                }
            }
            if (failedCount) alertError(`Deleted ${deletedCount} duplicate record(s); ${failedCount} could not be deleted.`, 'Cleanup Incomplete');
            else alertSuccess(`Cleanup Complete! Deleted ${deletedCount} duplicate records.`, 'Cleanup Done');
            await refreshDbRecords(true);
        } catch (e) {
            alertError('Cleanup failed: ' + e.message, 'Cleanup Error');
        }
        setLoading(false);
    };

    const handleRefresh = async () => {
        await refreshDbRecords(true);
        alertSuccess("Data Refreshed Successfully!", "Refresh Done");
    };

    const handleClearAll = async () => {
        if (!(await confirmDialog({
            title: 'DANGER: Clear All Maintenance Records?',
            message: 'This will delete ALL records. Cannot be undone. Confirm?',
            confirmLabel: 'Clear all',
            danger: true,
        }))) return;
        setLoading(true);
        try {
            const chunk = 50;
            let failed = 0;
            for (let i = 0; i < dbRecords.length; i += chunk) {
                const batch = dbRecords.slice(i, i + chunk);
                const results = await Promise.all(batch.map(r => maintenanceService.deleteLog(r.id)));
                failed += results.filter(res => !res || !res.success).length;   // deleteLog answers, it does not throw
            }
            if (failed) alertError(`${failed} record(s) could not be deleted.`, 'Clear Incomplete');
            else alertSuccess('Database cleared successfully!', 'Database Cleared');
            await refreshDbRecords();
        } catch (e) {
            alertError('Error: ' + e.message, 'Clear Failed');
        }
        setLoading(false);
    };

    const handleShowDelayed = () => {
        setFilterMode("delayed");
    };

    const handleAddRecord = async () => {

        if (!newRecord.Vehicle) {
            alertError('Please enter a Vehicle Plate Number', 'Missing Data');
            return;
        }

        if (!newRecord["Damage Details"] || !newRecord["Damage Details"].trim()) {
            alertError('Please enter Damage Details', 'Missing Data');
            return;
        }

        if (!newRecord["Date OUT"] || !newRecord["Date OUT"].trim()) {
            alertError('Please enter Date Out', 'Missing Data');
            return;
        }

        // STRICT VALIDATION: Check if vehicle exists in Fleet
        const cleanPlate = normalizePlate(newRecord.Vehicle);
        const fleetMatch = fleetData.find(c => normalizePlate(c.plate_number) === cleanPlate);

        if (!fleetMatch) {
            alertError(`Vehicle "${newRecord.Vehicle}" not found in Fleet database!`, 'Invalid Vehicle');
            return;
        }

        setLoading(true);

        // Auto-Status Logic
        const recordToAdd = { ...newRecord };
        // Set default status if not provided
        if (!recordToAdd.Status || recordToAdd.Status.trim() === "") {
            recordToAdd.Status = "Pending";
        }
        // Auto-complete status when Date IN is provided
        if (recordToAdd["Date IN"] && recordToAdd["Date IN"].trim() !== "") {
            recordToAdd.Status = "Completed";
        }

        const res = await maintenanceService.addLog(recordToAdd);

        if (res.success) {
            // Reset form
            setNewRecord({
                Vehicle: "",
                model: "",
                "Damage Details": "",
                "Date OUT": "",
                "Date IN": "",
                Status: lastExcelData.status || "",
                Branch: lastExcelData.branch || "",
                Date: formatDateDDMMYYYY() // Reset to today's date in DD/MM/YYYY
            });

            // Force refresh the data to show new record immediately
            await refreshDbRecords(true);

            // Show appropriate message based on operation
            const message = res.wasUpdated ? 'Record updated successfully!' : 'Record added successfully!';
            toast.success(message);

            // Log the operation type
            if (res.wasUpdated) {
                adminService.logUserAction('UPDATE_MAINTENANCE', 'Updated existing maintenance record', currentUser, {
                    vehicle: recordToAdd.Vehicle,
                    recordId: res.id
                });
            } else {
                adminService.logUserAction('ADD_MAINTENANCE', 'Added new maintenance record', currentUser, {
                    vehicle: recordToAdd.Vehicle,
                    recordId: res.id
                });
            }

            // alertSuccess('Record added to database successfully!');
        } else {
            alertError('Failed to add record: ' + res.error, 'Save Error');
        }
        setLoading(false);
    };

    const handleDeleteRecord = async (id) => {
        if (!(await confirmDialog({
            title: 'Delete record?',
            message: 'Are you sure you want to delete this record?',
            confirmLabel: 'Delete',
            danger: true,
        }))) return;
        setLoading(true);
        const res = await maintenanceService.deleteLog(id);
        if (res.success) {
            await refreshDbRecords(true);
        } else {
            alertError('Failed to delete record', 'Delete Error');
        }
        setLoading(false);
    };

    // const handleUpdateRecord = async (id, field, value) => {
    //     let updateData = { [field]: value };
    //     if (field === "Date IN" && value && value.trim() !== "") {
    //         updateData.Status = "Completed";
    //     }
    //     await maintenanceService.updateLog(id, updateData);
    //     refreshDbRecords();
    // };



    // Cache for Invygo plates
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    const invygoCache = useRef(null);
    const invygoCacheTimestamp = useRef(null);

    useEffect(() => {
        const fetchInvygoPlates = async () => {
            // Check cache
            const now = Date.now();
            if (invygoCache.current && invygoCacheTimestamp.current && (now - invygoCacheTimestamp.current) < CACHE_DURATION) {
                setInvygoPlates(invygoCache.current);
                return;
            }

            try {
                logger.info("Fetching Fleet DB for Invygo Check...");
                const allCars = await fleetService.getAllCars();
                setFleetData(allCars); // Store for Auto-Fill feature

                // Logic from Fleet.js: check 'make' or 'remarks'
                const invygoCars = allCars.filter(c =>
                    String(c.make || '').toLowerCase().includes('invygo') ||
                    String(c.remarks || '').toLowerCase().includes('invygo')
                );

                const plates = invygoCars.map(c => normalizePlate(c.plate_number)).filter(Boolean);

                logger.info(`Found ${plates.length} Invygo cars in Fleet DB.`);

                // Update cache
                invygoCache.current = plates;
                invygoCacheTimestamp.current = now;

                setInvygoPlates(plates);
            } catch (error) {
                logger.error("Error fetching Invygo plates from DB:", error);
            }
        };
        fetchInvygoPlates();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Load Status Map from DB on Mount
    useEffect(() => {
        const loadStatusMap = async () => {
            const result = await maintenanceService.getStatusMap();
            // Handle new structure { map, last_updated } or fallback
            const map = result.map || result || {};
            const lastUpdated = result.last_updated;

            if (map && Object.keys(map).length > 0) {
                setUploadedStatusMap(map);

                const first = Object.values(map)[0];
                if (first) {
                    setLastExcelData({ status: first.status || "", branch: first.branch || "" });
                }
            }
            if (lastUpdated) {
                setStatusLastUpdated(lastUpdated);
            }
        };
        loadStatusMap();
    }, []);

    // The cars in the workshop on Ejar (Maintenance status at Business Bay / Invygo - the engine's Overview rule) that
    // have no open record here yet (owner, 18 Oct 2026; Invygo since 29 Oct 2026)
    const [ejarWorkshop, setEjarWorkshop] = useState({ cars: null, error: '' });
    const loadEjarWorkshop = useCallback(() => {
        let alive = true;
        const day = dubaiToday();
        finesService.overviewList({ what: 'maintenance', from: day, to: day })
            .then((r) => { if (alive) setEjarWorkshop({ cars: Array.isArray(r?.items) ? r.items : [], error: '' }); })
            .catch((e) => { if (alive) setEjarWorkshop({ cars: null, error: errorMessage(e) }); });
        return () => { alive = false; };
    }, []);
    useEffect(() => loadEjarWorkshop(), [loadEjarWorkshop]);
    const workshopNoRecord = useMemo(
        () => (ejarWorkshop.cars ? carsWithoutOpenLog(ejarWorkshop.cars, dbRecords, normalizePlate) : []),
        [ejarWorkshop.cars, dbRecords]);

    // Create Fleet Map for O(1) Lookup
    const fleetMap = useMemo(() => {
        const map = {};
        fleetData.forEach(c => {
            const p = normalizePlate(c.plate_number);
            if (p) map[p] = c;
        });
        return map;
    }, [fleetData]);

    // The car's status in the fleet (Ejar's status, merged hourly) - for the "car is back" warning on an open log
    const fleetStatusOf = useCallback((plate) => fleetMap[normalizePlate(plate)]?.status || '', [fleetMap]);

    // Simplified Data Source (App Only) + Auto-Populate Model
    const combinedData = useMemo(() => {
        const result = dbRecords.map(r => {
            const plate = normalizePlate(r.Vehicle);
            // Use existing model from DB, or fallback to Fleet DB
            // Assuming Fleet Data has 'make' and 'model' or 'car_name'
            let autoModel = "";
            const fleetCar = fleetMap[plate];
            if (fleetCar) {
                const make = fleetCar.make || "";
                const model = fleetCar.model || fleetCar.car_model || "";
                autoModel = `${make} ${model} `.trim();
            }

            // Clean keys: remove carriage returns and merge duplicate fields
            const cleanedRow = {};
            Object.keys(r).forEach(key => {
                const cleanKey = key.replace(/\r/g, '').trim();
                // If clean key already exists, keep the non-empty value
                if (cleanedRow[cleanKey]) {
                    const existingValue = String(cleanedRow[cleanKey] || '').trim();
                    const newValue = String(r[key] || '').trim();
                    // Keep the value that's not empty or just whitespace
                    if (newValue && newValue !== '\r' && (!existingValue || existingValue === '\r')) {
                        cleanedRow[cleanKey] = r[key];
                    }
                } else {
                    cleanedRow[cleanKey] = r[key];
                }
            });

            return {
                ...cleanedRow,
                isDb: true,
                model: cleanedRow.model || autoModel || "",
                Branch: cleanedRow.Branch || "" // Add Branch field
            };
        });

        return result;
    }, [dbRecords, fleetMap]);

    useEffect(() => {
        const counts = {};
        const rowMap = {};
        combinedData.forEach((row, idx) => {
            const plate = normalizePlate(row["Vehicle"]);
            const dateInKey = Object.keys(row).find(k => k.toLowerCase().includes("date in"));
            const hasDateIn = dateInKey && row[dateInKey]?.trim() !== "";
            // Only count as duplicate if NO Date IN (incomplete maintenance)
            if (plate && !hasDateIn) {
                counts[plate] = (counts[plate] || 0) + 1;
                rowMap[plate] = rowMap[plate] || [];
                rowMap[plate].push(idx + 1);
            }
        });
        setDuplicatePlates(Object.fromEntries(Object.entries(counts).filter(([_, c]) => c > 1)));
        setDuplicateRows(rowMap);
    }, [combinedData]); // Depend only on DB changes

    const parseDate = (str) => {
        if (!str || typeof str !== "string") return new Date("Invalid");
        const formats = ["dd/MM/yyyy", "d-M-yyyy", "dd-MM-yyyy", "yyyy-MM-dd"];
        for (const format of formats) {
            const parsed = parse(str, format, new Date());
            if (!isNaN(parsed)) return parsed;
        }
        const fallback = new Date(str);
        return isNaN(fallback) ? new Date("Invalid") : fallback;
    };

    function getDisplayRows(rows) {
        return rows.map((row, i) => {
            const dateOutKey = Object.keys(row).find(k => k.toLowerCase().includes("date out"));
            const dateInKey = Object.keys(row).find(k => k.toLowerCase().includes("date in"));
            const dateOutStr = dateOutKey && row[dateOutKey];
            const dateInStr = dateInKey && row[dateInKey];
            let daysPassed = "";

            if (dateOutStr && (!dateInStr || dateInStr.trim() === "")) {
                const dateOut = parseDate(dateOutStr);
                if (!isNaN(dateOut)) {
                    const days = differenceInDays(new Date(), dateOut);
                    const damageKey = Object.keys(row).find(k => k.toLowerCase().includes("damag"));
                    const damage = damageKey && row[damageKey]?.toLowerCase();
                    if (damage?.includes("total loss")) {
                        daysPassed = "";
                    } else {
                        let requiredDays = [];
                        if (damage?.includes("oil")) requiredDays.push(3);
                        if (damage?.includes("accident")) requiredDays.push(30);
                        if (!damage?.includes("oil") && !damage?.includes("accident")) requiredDays.push(3);
                        const maxRequired = requiredDays.length ? Math.max(...requiredDays) : 3;
                        if (days > maxRequired) daysPassed = days - maxRequired; else daysPassed = "";
                    }
                }
            }
            return {
                Index: i + 1,
                ...row,
                "Days Delayed": daysPassed,
                Notes: getNote(row)
            };
        });
    }

    // Get unique status values from data
    const uniqueStatuses = useMemo(() => {
        const statuses = new Set();
        combinedData.forEach(row => {
            if (row.Status) {
                statuses.add(String(row.Status).replace(/\r/g, '').trim());
            }
        });
        return Array.from(statuses).filter(Boolean).sort();
    }, [combinedData]);

    let filtered = [];
    // First, filter by tab
    let baseData = combinedData;
    if (activeTab === 'completed') {
        baseData = combinedData.filter(row => {
            const dateInKey = Object.keys(row).find(k => k.toLowerCase().includes("date in"));
            return dateInKey && row[dateInKey]?.trim() !== "";
        });
    } else if (activeTab === 'incomplete') {
        baseData = combinedData.filter(row => {
            const dateInKey = Object.keys(row).find(k => k.toLowerCase().includes("date in"));
            return !dateInKey || !row[dateInKey]?.trim();
        });
    }

    if (search.trim() !== "") {
        const baseRows = baseData.filter(row =>
            Object.values(row).some(v => typeof v === 'string' && v.toLowerCase().includes(search.toLowerCase()))
        );
        filtered = getDisplayRows(baseRows).map((row, i) => ({ ...row, Index: i + 1 }));
    } else if (filterMode === "delayed") {
        const delayedRows = getDisplayRows(baseData).filter(row => row["Days Delayed"] !== "");
        filtered = delayedRows.map((row, i) => ({ ...row, Index: i + 1 }));
    } else if (filterMode === "totalloss") {
        const totalLossRows = baseData.filter(row =>
            Object.values(row).some(v => typeof v === 'string' && v.toLowerCase().includes('total loss'))
        );
        filtered = getDisplayRows(totalLossRows).map((row, i) => ({ ...row, Index: i + 1 }));
    } else {
        const baseRows = baseData.filter(row => {
            const vehicle = row["Vehicle"]?.trim();
            if (!vehicle || vehicle === "#N/A" || vehicle === "") return false;
            // Basic data check
            const dateInKey = Object.keys(row).find(k => k.toLowerCase().includes("date in"));
            const damageKey = Object.keys(row).find(k => k.toLowerCase().includes("damag"));
            const damageText = damageKey && row[damageKey]?.toLowerCase();
            const hasDate = dateInKey && row[dateInKey]?.trim() !== "";
            const plate = normalizePlate(row["Vehicle"]);
            if (filterMode === "accident") return damageText?.includes("accident");
            if (filterMode === "invygo") return plate && invygoPlates.includes(plate);
            if (filterMode === "ready") return hasDate;
            if (filterMode === "notready") {
                const dateOutKey = Object.keys(row).find(k => k.toLowerCase().includes("date out"));
                const hasDateOut = dateOutKey && row[dateOutKey]?.trim() !== "";
                const hasTotalLoss = Object.values(row).some(v => typeof v === 'string' && v.toLowerCase().includes('total loss'));
                return hasDateOut && !hasDate && !hasTotalLoss;
            }
            if (filterMode === "duplicates") {
                if (!plate || !duplicatePlates[plate]) return false;
                const dateIn = dateInKey && row[dateInKey]?.trim();
                return !dateIn;
            }
            return true;
        });
        filtered = getDisplayRows(baseRows).map((row, i) => ({ ...row, Index: i + 1 }));
    }

    // Apply Status Filter
    if (statusFilter) {
        filtered = filtered.filter(row => row.Status === statusFilter);
    }

    // Sort by Date (Desc) - Registration/Entry Date - NEW RECORDS FIRST
    filtered.sort((a, b) => {
        // New records (without id) should appear first
        if (!a.id && b.id) return -1;
        if (a.id && !b.id) return 1;

        // Use created_at timestamp for more accurate sorting, fallback to Date field
        const timestampA = a.created_at ? new Date(a.created_at) : (a["Date"] ? parseDate(a["Date"]) : new Date(0));
        const timestampB = b.created_at ? new Date(b.created_at) : (b["Date"] ? parseDate(b["Date"]) : new Date(0));
        return timestampB - timestampA; // Descending (newest first)
    });

    // Re-assign Index to be sequential after sorting
    filtered = filtered.map((row, i) => ({ ...row, Index: i + 1 }));

    function getNote(row, _rowIndex = 0) {
        const plate = normalizePlate(row["Vehicle"]);
        const carType = invygoPlates.includes(plate) ? "Invygo" : "Yelo";
        let status = "";
        if (row["Days Delayed"]) status = "Delayed";
        else {
            const damageKey = Object.keys(row).find(k => k.toLowerCase().includes("damag"));
            const damageText = damageKey && row[damageKey]?.toLowerCase();
            const dateInKey = Object.keys(row).find(k => k.toLowerCase().includes("date in"));
            const hasDate = dateInKey && row[dateInKey]?.trim() !== "";
            if (damageText?.includes("accident")) status = "Accident";
            else if (hasDate) status = "Repaired";
            else status = "Not Repaired";
        }
        if (plate && duplicatePlates[plate]) {
            status += `, Duplicate`;
        }
        return `${carType} ${status} `;
    }

    const exportToExcel = () => {
        let fileName = "Maintenance";
        if (search.trim() !== "") fileName = "search";
        else if (filterMode !== "all") fileName = filterMode;

        // (`version`: the store's save version, USE_CONTRACTS_V2 - not a column)
        const exportRows = filterColumnsWithData(filtered.map(({ version, ...row }, i) => ({ ...row, Notes: getNote(row, i) })));
        const ws = XLSX.utils.json_to_sheet(exportRows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, fileName);
        XLSX.writeFile(wb, `${fileName}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };



    // Calculate Counts
    const invygoCount = combinedData.filter(row => {
        const plate = normalizePlate(row["Vehicle"]);
        const dateInKey = Object.keys(row).find(k => k.toLowerCase().includes("date in"));
        const hasDate = dateInKey && row[dateInKey]?.trim() !== "";
        return plate && invygoPlates.includes(plate) && !hasDate;
    }).length;

    const accidentCount = combinedData.filter(row => {
        const damageKey = Object.keys(row).find(k => k.toLowerCase().includes("damag"));
        const damageText = damageKey && row[damageKey]?.toLowerCase();
        const dateInKey = Object.keys(row).find(k => k.toLowerCase().includes("date in"));
        const hasDate = dateInKey && row[dateInKey]?.trim() !== "";
        return damageText?.includes("accident") && !hasDate;
    }).length;

    const notReadyCount = combinedData.filter(row => {
        const dateInKey = Object.keys(row).find(k => k.toLowerCase().includes("date in"));
        const dateOutKey = Object.keys(row).find(k => k.toLowerCase().includes("date out"));
        const hasDateIn = dateInKey && row[dateInKey]?.trim() !== "";
        const hasDateOut = dateOutKey && row[dateOutKey]?.trim() !== "";
        const hasTotalLoss = Object.values(row).some(v => typeof v === 'string' && v.toLowerCase().includes('total loss'));
        return hasDateOut && !hasDateIn && !hasTotalLoss;
    }).length;

    const duplicatesCount = Object.values(duplicatePlates).reduce((a, b) => a + b, 0);
    const delayedCount = getDisplayRows(combinedData).filter(row => row["Days Delayed"] !== "").length;

    // Total Loss Calculation
    const totalLossCount = combinedData.filter(row =>
        Object.values(row).some(v => typeof v === 'string' && v.toLowerCase().includes('total loss'))
    ).length;

    const allRowsCount = combinedData.length;

    // ... GAPI Init (Keep same) ...


    // Date IN typed in the table: written once, when the date is whole (the input fires on every
    // digit of the year), the write is awaited and checked, and only that row is patched on screen
    // instead of re-reading every log after each keystroke (audit FLT-16).
    const handleEdit = useCallback(async (row, raw) => {
        if (!row || !row.isDb || !row.id) return false;
        const updates = dateInUpdate(raw);
        if (!updates) {
            toast.error('Please enter a full date (DD/MM/YYYY).');
            return false;
        }
        // the store: saved on the version the row was read with; the row takes the log's new version (the next
        // edit names it) - changed by someone else since, nothing is saved and the logs are read again
        const res = await maintenanceService.updateLog(row.id, updates, { version: row.version });
        if (!res || !res.success) {
            if (res && res.code === 'record-changed') maintenanceService.getAllLogs(true).then(setDbRecords).catch(() => {});
            alertError('Failed to save Date IN: ' + ((res && res.error) || 'no reply from the server'), 'Save Error');
            return false;
        }
        const version = res.version;
        setDbRecords((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...updates, ...(version !== undefined ? { version } : {}) } : r)));
        return true;
    }, [alertError]);

    const handleVehicleChange = (e) => {
        const val = e.target.value;
        setNewRecord({ ...newRecord, Vehicle: val });
        setVehicleWarning(null);
        setSuggestions([]);

        if (searchTimeout.current) clearTimeout(searchTimeout.current);

        if (!val || val.length < 2) return;

        searchTimeout.current = setTimeout(() => {
            const inputRaw = val.trim().toLowerCase();
            const isNumeric = /^\d+$/.test(inputRaw);

            const matches = fleetData.filter(c => {
                const p = normalizePlate(c.plate_number);
                if (isNumeric) {
                    const pNums = p.replace(/\D/g, "");
                    return pNums.includes(inputRaw);
                } else {
                    return p.includes(inputRaw.replace(/\s+/g, ""));
                }
            });

            const cleanPlate = normalizePlate(val);

            // Determine target plate for lookup
            let targetPlate = cleanPlate;
            if (matches.length > 0) {
                targetPlate = normalizePlate(matches[0].plate_number);
            }

            // AUTO-POPULATE STATUS/BRANCH
            let autoStatus = "";
            let autoBranch = "";

            const mapData = uploadedStatusMap[targetPlate];

            if (mapData) {
                autoStatus = mapData.status || "";
                autoBranch = mapData.branch || "";
            } else {
                const existingDB = dbRecords.find(r => normalizePlate(r.Vehicle) === targetPlate);
                if (existingDB && existingDB.Status) {
                    autoStatus = existingDB.Status;
                    autoBranch = existingDB.Branch || "";
                }
            }

            if (matches.length === 1) {
                const car = matches[0];
                const matchedModel = `${car.make || ''} ${car.model || ''} `.trim();
                const resolvedPlate = normalizePlate(car.plate_number);
                const existingDB = dbRecords.find(r => normalizePlate(r.Vehicle) === resolvedPlate && !r["Date IN"]);

                setNewRecord(prev => ({
                    ...prev,
                    Vehicle: car.plate_number,
                    model: matchedModel,
                    Status: autoStatus,
                    Branch: autoBranch
                }));

                if (existingDB) {
                    setVehicleWarning(`Warning: ${car.plate_number} is already in maintenance`);
                }
            } else if (matches.length > 1) {
                setSuggestions(matches.slice(0, 5));
            } else {
                const cleanInput = normalizePlate(val);
                const existingDB = dbRecords.find(r => normalizePlate(r.Vehicle) === cleanInput && !r["Date IN"]);

                setNewRecord(prev => ({
                    ...prev,
                    Status: autoStatus,
                    Branch: autoBranch
                }));

                if (existingDB) {
                    setVehicleWarning(`Warning: Already in maintenance`);
                }
            }
        }, 1500);
    };

    // "Add record" on a car the workshop list shows: fills the form with it (Ejar's car, model and branch)
    const addFormRef = useRef(null);
    const startRecordFor = (row) => {
        const car = fleetData.find((c) => normalizePlate(c.plate_number) === normalizePlate(row.plate));
        if (car) handlePickSuggestion(car);
        else setNewRecord((prev) => ({ ...prev, Vehicle: row.plate, model: `${row.make || ''} ${row.model || ''}`.trim() }));
        setNewRecord((prev) => ({ ...prev, Branch: prev.Branch || row.branch || '' }));
        addFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handlePickSuggestion = (car) => {
        const autoModel = `${car.make || ''} ${car.model || ''} `.trim();
        const cleanPlate = normalizePlate(car.plate_number);
        const existingDB = dbRecords.find(r => normalizePlate(r.Vehicle) === cleanPlate && !r["Date IN"]);

        let autoStatus = "";
        let autoBranch = "";
        const mapData = uploadedStatusMap[cleanPlate];

        if (mapData) {
            autoStatus = mapData.status || "";
            autoBranch = mapData.branch || "";
        } else {
            const dbMatch = dbRecords.find(r => normalizePlate(r.Vehicle) === cleanPlate);
            if (dbMatch && dbMatch.Status) {
                autoStatus = dbMatch.Status;
                autoBranch = dbMatch.Branch || "";
            }
        }

        setNewRecord(prev => ({
            ...prev,
            Vehicle: car.plate_number,
            model: autoModel,
            Status: autoStatus,
            Branch: autoBranch
        }));

        if (existingDB) {
            setVehicleWarning(`Warning: ${car.plate_number} is already in maintenance`);
        } else {
            setVehicleWarning(null);
        }
        setSuggestions([]);
    };

    return (
        <Layout title="Cars Maintenance">
            <Card className="mb-4" padding="none">
                {/* Section 1: Add New Record (Permission Gated) */}
                <RequirePermission permission={PERMISSIONS.add_maintenance_record} fallback={null}>
                    <div className="p-4 border-b border-gray-200" ref={addFormRef}>
                        <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                            <h3 className="text-base font-semibold flex items-center gap-2 text-gray-900">
                                <Icon glyph={ICONS.maintenance} size="sm" tone="muted" />
                                Add New Maintenance Record
                            </h3>
                            {statusLastUpdated && (
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span>Last status update</span>
                                    <span className="font-mono text-gray-700">
                                        {dubaiDateTime(statusLastUpdated)}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-3 mb-3">
                            <Field label="Date">
                                <Input
                                    placeholder="DD/MM/YYYY"
                                    value={newRecord.Date}
                                    onChange={e => setNewRecord({ ...newRecord, Date: e.target.value })}
                                />
                            </Field>

                            <div className="ds-field relative">
                                <label className="ds-field__label" htmlFor="cm-vehicle">
                                    Vehicle<span className="ds-field__required" aria-hidden="true">*</span>
                                </label>
                                <Input
                                    id="cm-vehicle"
                                    required
                                    placeholder="Plate No."
                                    value={newRecord.Vehicle}
                                    onChange={handleVehicleChange}
                                    invalid={Boolean(vehicleWarning)}
                                    aria-describedby={vehicleWarning ? 'cm-vehicle-warning' : undefined}
                                    autoComplete="off"
                                />
                                {suggestions.length > 0 && (
                                    <div className="absolute z-50 top-full left-0 w-full bg-white border border-gray-200 rounded-lg shadow-md mt-1 max-h-60 overflow-y-auto">
                                        <div className="px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-50 border-b border-gray-200">Did you mean?</div>
                                        {suggestions.map((car, idx) => (
                                            <button
                                                type="button"
                                                key={idx}
                                                onClick={() => handlePickSuggestion(car)}
                                                className="w-full text-left px-3 py-2 hover:bg-gray-50 focus:bg-gray-50 text-sm border-b last:border-0 border-gray-100"
                                            >
                                                <span className="font-mono font-medium text-gray-900">{car.plate_number}</span>
                                                <span className="ml-2 text-xs text-gray-500">({car.make} {car.model})</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <Field label="Model">
                                <Input
                                    placeholder="Auto-filled"
                                    value={newRecord.model}
                                    readOnly
                                    tabIndex={-1}
                                />
                            </Field>

                            <Field label="Damage Details" required className="lg:col-span-2">
                                <Input
                                    placeholder="Damage Details"
                                    value={newRecord["Damage Details"]}
                                    onChange={e => setNewRecord({ ...newRecord, "Damage Details": e.target.value })}
                                />
                            </Field>

                            <Field label="Date Out" required>
                                <Input
                                    type="text"
                                    onFocus={(e) => { e.target.type = 'date'; if (newRecord["Date OUT"]) e.target.value = toInputFormat(newRecord["Date OUT"]); }}
                                    onBlur={(e) => { e.target.type = 'text'; e.target.value = newRecord["Date OUT"]; }}
                                    placeholder="Date Out"
                                    value={newRecord["Date OUT"]}
                                    onChange={e => {
                                        const val = e.target.value;
                                        const formatted = toDisplayFormat(val);
                                        setNewRecord({ ...newRecord, "Date OUT": formatted });
                                    }}
                                />
                            </Field>

                            <Field label="Date In">
                                <Input
                                    type="text"
                                    onFocus={(e) => { e.target.type = 'date'; if (newRecord["Date IN"]) e.target.value = toInputFormat(newRecord["Date IN"]); }}
                                    onBlur={(e) => { e.target.type = 'text'; e.target.value = newRecord["Date IN"]; }}
                                    placeholder="Date In"
                                    value={newRecord["Date IN"]}
                                    onChange={e => {
                                        const val = e.target.value;
                                        const formatted = toDisplayFormat(val);
                                        setNewRecord({ ...newRecord, "Date IN": formatted });
                                    }}
                                />
                            </Field>

                            <Field label="Branch">
                                <Input
                                    placeholder="Branch"
                                    value={newRecord.Branch}
                                    onChange={e => setNewRecord({ ...newRecord, Branch: e.target.value })}
                                />
                            </Field>
                        </div>

                        {vehicleWarning && (
                            <div id="cm-vehicle-warning" role="alert" className="mb-3 px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded-md text-sm flex items-center gap-2">
                                <Icon glyph={ICONS.warning} size="sm" />
                                <span>{vehicleWarning}</span>
                            </div>
                        )}

                        <div className="ds-toolbar justify-end">
                            {/* Database tools grouped into one menu; Save stays visible */}
                            <ActionMenu
                                label="Data & tools"
                                icon={ICONS.refresh}
                                size="sm"
                                items={[
                                    {
                                        label: 'Refresh',
                                        icon: ICONS.refresh,
                                        onClick: handleRefresh,
                                    },
                                    {
                                        label: 'Clean Duplicates',
                                        icon: ICONS.warning,
                                        onClick: handleCleanupDuplicates,
                                        hidden: !(dbDuplicates.length > 0) || !hasPermission(PERMISSIONS.delete_maintenance_record),
                                    },
                                    { divider: true },
                                    {
                                        label: 'Clear All',
                                        icon: ICONS.delete,
                                        danger: true,
                                        onClick: handleClearAll,
                                        hidden: !hasPermission(PERMISSIONS.reset_maintenance_db),
                                    },
                                ]}
                            />

                            <RequirePermission permission={PERMISSIONS.save_maintenance_db} fallback={null}>
                                <Button onClick={handleAddRecord} variant="primary" size="sm" leadingIcon={<Icon glyph={ICONS.save} size="sm" />}>
                                    Save
                                </Button>
                            </RequirePermission>
                        </div>
                    </div>
                </RequirePermission>

                {/* Section 2: Tools & Filters */}
                <div className="p-4 flex flex-wrap justify-between items-end gap-4">
                    <div className="flex flex-wrap items-end gap-4">
                        <FilterButtons
                            filterMode={filterMode} setFilterMode={setFilterMode}
                            accidentCount={accidentCount} invygoCount={invygoCount}
                            notReadyCount={notReadyCount} duplicatesCount={duplicatesCount}
                            delayedCount={delayedCount} search={search} setSearch={setSearch}
                            allRowsCount={allRowsCount} totalLossCount={totalLossCount}
                            statusFilter={statusFilter} setStatusFilter={setStatusFilter}
                            uniqueStatuses={uniqueStatuses}
                        />

                        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg items-center" role="group" aria-label="Completion">
                            <RequirePermission permission={PERMISSIONS.view_incomplete_maintenance} fallback={null}>
                                <Button
                                    onClick={() => setActiveTab('incomplete')}
                                    variant={activeTab === 'incomplete' ? 'secondary' : 'ghost'}
                                    size="sm"
                                    aria-pressed={activeTab === 'incomplete'}
                                >
                                    Incomplete
                                </Button>
                            </RequirePermission>

                            <RequirePermission permission={PERMISSIONS.view_completed_maintenance} fallback={null}>
                                <Button
                                    onClick={() => setActiveTab('completed')}
                                    variant={activeTab === 'completed' ? 'secondary' : 'ghost'}
                                    size="sm"
                                    aria-pressed={activeTab === 'completed'}
                                >
                                    Completed
                                </Button>
                            </RequirePermission>
                        </div>
                    </div>

                    <div className="ds-toolbar">
                        {delayedCount > 0 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-700"
                                onClick={handleShowDelayed}
                                title="Show only delayed cars"
                                leadingIcon={<Icon glyph={ICONS.alert} size="sm" tone="danger" />}>
                                {delayedCount} Delayed
                            </Button>
                        )}
                        <span className="text-sm text-gray-600 whitespace-nowrap">Count: {filtered.length}</span>
                        <RequirePermission permission={PERMISSIONS.export_maintenance} fallback={null}>
                            <ExportButtons exportToExcel={exportToExcel} />
                        </RequirePermission>
                    </div>
                </div>
            </Card>

            <EjarWorkshopCard
                cars={workshopNoRecord}
                total={ejarWorkshop.cars ? ejarWorkshop.cars.length : null}
                error={ejarWorkshop.error}
                onRetry={loadEjarWorkshop}
                onAdd={hasPermission(PERMISSIONS.add_maintenance_record) ? startRecordFor : null}
            />

            {loading && dbRecords.length === 0 ? (
                <LoadingSpinner message="Loading maintenance records…" />
            ) : (
                <MaintenanceTable
                    filtered={filtered}
                    getDisplayRows={getDisplayRows}
                    invygoPlates={invygoPlates}
                    duplicatePlates={duplicatePlates}
                    duplicateRows={duplicateRows}
                    getNote={getNote}
                    handleEdit={handleEdit}
                    canEditDateIn={hasPermission(PERMISSIONS.edit_maintenance_record)}
                    fleetStatusOf={fleetStatusOf}
                    handleDelete={handleDeleteRecord}
                    onEdit={handleEditTask}
                />
            )}

            {loading && dbRecords.length > 0 && (
                <div className="flex items-center justify-center gap-2 my-6 text-sm text-gray-600">
                    <LoadingSpinner size="sm" message="Processing" /> Processing…
                </div>
            )}

            {/* Task Edit Modal */}
            <Dialog
                open={Boolean(isEditModalOpen && editingRecord)}
                onClose={() => setIsEditModalOpen(false)}
                title="Edit Task"
                description={editingRecord ? editingRecord.Vehicle : undefined}
                size="sm"
                closeOnOverlay={false}
                footer={(
                    <>
                        <Button onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
                        <Button onClick={handleSaveEdit} variant="primary" leadingIcon={<Icon glyph={ICONS.save} size="sm" />}>
                            Save Changes
                        </Button>
                    </>
                )}
            >
                {editingRecord && (
                    <Field label="Damage Details / Task" required>
                        <Textarea
                            value={editingRecord["Damage Details"] || ""}
                            onChange={e => setEditingRecord({ ...editingRecord, "Damage Details": e.target.value })}
                            rows={5}
                            placeholder="Enter task details..."
                            data-autofocus
                        />
                    </Field>
                )}
            </Dialog>
        </Layout>
    );
}

function FilterButtons({ filterMode, setFilterMode, search, setSearch, allRowsCount, accidentCount, invygoCount, notReadyCount, duplicatesCount, delayedCount, totalLossCount, statusFilter, setStatusFilter, uniqueStatuses }) {
    return (
        <div className="flex flex-wrap items-end gap-3">
            <Field label="Filter" className="w-full sm:w-52">
                <Select value={filterMode} onChange={e => setFilterMode(e.target.value)}>
                    <option value="all">Show All ({allRowsCount})</option>
                    <option value="accident">Accident ({accidentCount})</option>
                    <option value="invygo">Invygo Cars ({invygoCount})</option>
                    <option value="notready">Not Ready ({notReadyCount})</option>
                    <option value="duplicates">Duplicates ({duplicatesCount})</option>
                    <option value="delayed">Delayed ({delayedCount})</option>
                    <option value="totalloss">Total Loss ({totalLossCount})</option>
                </Select>
            </Field>

            <Field label="Status" className="w-full sm:w-44">
                <Select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    placeholder="All Status"
                    options={uniqueStatuses}
                />
            </Field>

            <div className="ds-field w-full sm:w-60">
                <label className="ds-field__label" htmlFor="cm-search">Search</label>
                <div className="ds-search" style={{ flex: 'none' }}>
                    <Icon glyph={ICONS.search} size="sm" />
                    <input
                        id="cm-search"
                        type="search"
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>
        </div>
    );
}

function ExportButtons({ exportToExcel }) {
    return (
        <Button onClick={exportToExcel} size="sm" leadingIcon={<Icon glyph={ICONS.export} size="sm" />}>
            Export
        </Button>
    );
}

/**
 * Date IN cell. The date input fires onChange for every digit of the year (0002, 0020, 0202, 2026),
 * so the value is kept here and saved once: when the field loses focus or Enter is pressed.
 */
function DateInInput({ row, value, onCommit }) {
    const saved = value ? toInputFormat(value) : '';
    const [draft, setDraft] = useState(saved);
    const [saving, setSaving] = useState(false);
    useEffect(() => { setDraft(saved); }, [saved]);

    const commit = async () => {
        if (saving || draft === saved) return;
        setSaving(true);
        const ok = await onCommit(row, draft);
        setSaving(false);
        if (!ok) setDraft(saved);
    };

    return (
        <input
            type="date"
            aria-label={`Date IN for ${row.Vehicle || 'vehicle'}`}
            className="ds-input ds-input--sm font-mono"
            style={{ minWidth: 140 }}
            value={draft}
            disabled={saving}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            onClick={(e) => e.stopPropagation()} // Prevent row click issues
        />
    );
}

function MaintenanceTable({ filtered, handleEdit, canEditDateIn = false, fleetStatusOf = () => '', handleDelete, onEdit, invygoPlates, duplicatePlates: _duplicatePlates }) {

    const columns = useMemo(() => {
        if (!filtered.length) return [];

        const sample = filtered[0];
        const allKeys = Object.keys(sample).filter(k =>
            k !== 'Index' && k !== 'Notes' && k !== 'Days Delayed' && k !== 'id' && k !== 'isDb' && k !== 'created_at' && k !== 'last_updated' && k !== 'originalIndex' && k !== 'data_hash' && k !== 'currentKm'
            && k !== 'version'   // the store's save version (USE_CONTRACTS_V2): not a column
        ).map(k => k.trim());

        const cleanKeys = [...new Set(allKeys)].filter(k => {
            if (k.toLowerCase().includes('status')) {
                return k === 'Status';
            }
            return true;
        });

        const preferredOrder = ["Date", "Vehicle", "model", "Damage Details", "Date OUT", "Date IN", "Status", "Branch"];
        return ["Index",
            ...preferredOrder.filter(k => cleanKeys.includes(k)),
            ...cleanKeys.filter(k => !preferredOrder.includes(k)), "Days Delayed", "Notes", "Actions"
        ];
    }, [filtered]);

    // Editable columns constant defined outside or memoized
    const editableColumns = useMemo(() => ['Date IN'], []);

    const renderCell = useCallback((row, col) => {
        let val = row[col] || "";

        // Fix Status column - handle all variations
        if (col === 'Status') {
            // Check all possible Status field variations
            const statusVariations = ['Status', 'Status\r', 'status', 'STATUS'];
            for (const variation of statusVariations) {
                if (row[variation]) {
                    val = String(row[variation]).replace(/\r/g, '').trim();
                    break;
                }
            }
        }
        // Date IN is typed in the table only with the same permission as the row's Edit button - it showed
        // to everyone who could open the page (a viewer's date write was refused by the database).
        if (editableColumns.includes(col) && canEditDateIn) {
            return <DateInInput row={row} value={val} onCommit={handleEdit} />;
        } else if (col === 'Vehicle') {
            const isInvygo = invygoPlates.includes(normalizePlate(val));
            // an open log whose car Ejar already shows Ready / Rented: the log was never closed (owner, 26 Sep 2026)
            const backWarning = openLogFleetWarning(row, fleetStatusOf(val));
            return (
                <div className="flex flex-col items-center justify-center gap-1 text-center">
                    <CellPlate tone={isInvygo ? 'info' : undefined}>{val}</CellPlate>
                    {isInvygo && <CellMuted>Invygo car</CellMuted>}
                    {backWarning && (
                        <span role="note" className="inline-flex items-center justify-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-900 text-center">
                            <Icon glyph={ICONS.warning} size="sm" tone="warning" />
                            {backWarning}
                        </span>
                    )}
                </div>
            );
        } else if (col === 'Status') {
            // Completed green, Pending orange, In Progress blue (as before the unify pass)
            const MAINT_STATUS_TONES = { Completed: 'success', Pending: 'warning', 'In Progress': 'info' };
            return val ? <CellStatus status={val} label={val} tone={MAINT_STATUS_TONES[val] || 'neutral'} /> : null;
        } else if (col === 'Days Delayed') {
            if (!val) return null;
            return <span className={`ds-cell-num ${toneClass('danger')}`}>{val} Days</span>;
        } else if (col === 'Date OUT' || col === 'Date' || col === 'Date IN') {
            return (
                <span className="ds-cell-date" title={String(val || '').replace(/[<>"']/g, '')}>
                    {toDisplayFormat(val)}
                </span>
            );
        } else {
            return (
                <CellText max={200} title={String(val || '').replace(/[<>"']/g, '')}>
                    {val}
                </CellText>
            );
        }
    }, [handleEdit, canEditDateIn, fleetStatusOf, invygoPlates, editableColumns]);

    if (!filtered.length) {
        return (
            <Card>
                <EmptyState
                    icon={ICONS.search}
                    title="No maintenance records"
                    description="No results match your search or filter."
                />
            </Card>
        );
    }

    return (
        <TableContainer maxHeight="70vh" className="overflow-auto">
            <Table sticky style={{ tableLayout: 'auto' }}>
                <TableHead>
                    <TableRow>
                        {columns.map(col => (
                            <TableHeader key={col} className="whitespace-nowrap">
                                {col === 'Actions' ? <span className="sr-only">Actions</span> : col}
                            </TableHeader>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filtered.map((row) => {
                        const rowKey = row.id || `sheet_${row.originalIndex}_${row.Vehicle} `;
                        return (
                            <TableRow key={rowKey}>
                                {columns.map(col => {
                                    if (col === 'Actions') {
                                        return (
                                            <TableCell key={col}>
                                                <div className="flex gap-1">
                                                    <RequirePermission permission={PERMISSIONS.edit_maintenance_record} fallback={null}>
                                                        <IconButton
                                                            icon={ICONS.edit}
                                                            label="Edit Task"
                                                            size="sm"
                                                            onClick={() => onEdit(row)}
                                                        />
                                                    </RequirePermission>
                                                    <RequirePermission permission={PERMISSIONS.delete_maintenance_record} fallback={null}>
                                                        <IconButton
                                                            icon={ICONS.delete}
                                                            label="Delete"
                                                            size="sm"
                                                            tone="danger"
                                                            onClick={() => handleDelete(row.id)}
                                                        />
                                                    </RequirePermission>
                                                </div>
                                            </TableCell>
                                        );
                                    }
                                    return (
                                        <TableCell key={col}>
                                            {renderCell(row, col)}
                                        </TableCell>
                                    )
                                })}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}
